import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { SessionService } from './session.service';
import { environment } from '../../environments/environment';

interface UserProfile {
  company?: {
    id: string;
  };
  applications?: {
    name: string;
    id: string;
  }[];
}

interface LicenseResponse {
  [key: string]: {
    apiKeys?: {
      key: string;
      [key: string]: any;
    }[];
    [key: string]: any;
  };
}

interface DecodedToken {
  APIDashBoardEntitlement?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class LicenseService {
  private profileCache: { data: UserProfile; timestamp: number } | null = null;
  private licenseCache: { data: string; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(private apiService: ApiService, private sessionService: SessionService) {}

  private getDecodedToken(): DecodedToken | null {
    const token = this.getAuthToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decodedPayload = atob(paddedPayload);
      return JSON.parse(decodedPayload) as DecodedToken;
    } catch (error) {
      return null;
    }
  }

  private getAuthToken(): string | undefined {
    const m = document.cookie.match(/(?:^|; )devhub_tkn=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : undefined;
  }

  private getEntitlements(): string[] {
    const decoded = this.getDecodedToken();
    if (!decoded) return [];

    const entitlementValue = decoded.APIDashBoardEntitlement;
    if (!entitlementValue) return [];

    if (Array.isArray(entitlementValue)) {
      return entitlementValue;
    }

    if (typeof entitlementValue === 'string') {
      return entitlementValue.split(',').map((e) => e.trim());
    }

    return [];
  }

  private getCredentialBaseUrl(): string {
    const entitlements = this.getEntitlements();

    const hasLiveEntitlement = entitlements.some((e) => e.toLowerCase().includes('live'));

    if (hasLiveEntitlement) {
      return this.getLiveBaseUrl();
    }

    const specialEntitlements = [
      'API_DASHBOARD_EVENTS_COMPANY_ADVANCED_USER',
      'API_DASHBOARD_EVENTS_COMPANY_ADMIN',
      'API_DASHBOARD_EVENTS_COMPANY_BASIC_USER',
    ];

    const hasSpecialEntitlement = entitlements.some((e) => specialEntitlements.includes(e));

    if (hasSpecialEntitlement) {
      return this.getOtherEntitlementsBaseUrl();
    }

    let hasTryUserEntitlement = false;

    if (entitlements.length === 1 && entitlements[0] === 'API_DASHBOARD_TRY_USER') {
      hasTryUserEntitlement = true;
    }

    if (entitlements.some((e) => specialEntitlements.includes(e))) {
      hasTryUserEntitlement = false;
    }

    if (hasTryUserEntitlement) {
      return this.getTryUserBaseUrl();
    }

    return this.getOtherEntitlementsBaseUrl();
  }

  private getLiveBaseUrl(): string {
    if (environment.production) {
      return 'https://eapi.fisglobal.com';
    }

    if (environment.envName === 'staging') {
      return 'https://cert-eapi.fisglobal.com';
    }

    return 'https://test-eapi.fisglobal.com';
  }

  private getTryUserBaseUrl(): string {
    if (environment.production) {
      return 'https://eapi.fisglobal.com';
    }

    return 'https://dev-eapi.fisglobal.com';
  }

  private getOtherEntitlementsBaseUrl(): string {
    if (environment.production) {
      return 'https://eapi.fisglobal.com';
    }

    if (environment.envName === 'staging') {
      return 'https://cert-eapi.fisglobal.com';
    }

    return 'https://dev-eapi.fisglobal.com';
  }

  private buildCredentialUrl(endpoint: string): string {
    const baseUrl = this.getCredentialBaseUrl();
    return `${baseUrl}${endpoint}`;
  }

  getUserLicenseKey(): Observable<string> {
    if (!this.sessionService.getUserInfo()) {
      return of('');
    }

    if (this.licenseCache && this.isCacheValid(this.licenseCache)) {
      return of(this.licenseCache.data);
    }

    return this.getUserProfile().pipe(
      switchMap((profile) => {
        if (!profile) {
          return of('');
        }

        const companyId = profile?.company?.id;
        const applicationName =
          profile?.applications && profile.applications.length > 0 ? profile.applications[0].name : null;

        return this.fetchLicenseKey(companyId, applicationName);
      }),
      catchError(() => {
        return of('');
      })
    );
  }

  private getUserProfile(): Observable<UserProfile | null> {
    if (this.profileCache && this.isCacheValid(this.profileCache)) {
      return of(this.profileCache.data);
    }

    const profileUrl = this.buildCredentialUrl('/dashboard-user-management-api/v1/users/self');

    return this.apiService.get(profileUrl, {}).pipe(
      map((response: any) => {
        this.profileCache = {
          data: response,
          timestamp: Date.now(),
        };
        return response;
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  private fetchLicenseKey(companyId: string, applicationName: string | null): Observable<string> {
    const licenseUrl = this.buildCredentialUrl('/license-management-api/v1/licenses/search');

    const postData = {
      companyCode: 'WORLDPAY',
      companyId: companyId,
      applicationNames: applicationName ? [applicationName] : null,
      idpReferenceId: null,
      identityProvider: 'INFINITY_TRY_EXPERIENCE',
    };

    return this.apiService.post(licenseUrl, postData).pipe(
      map((response: LicenseResponse) => {
        if (response && Object.keys(response).length > 0) {
          const firstKey = Object.keys(response)[0];
          const apiKeys = response[firstKey]?.apiKeys;

          if (apiKeys && Array.isArray(apiKeys) && apiKeys.length > 0) {
            const licenseKey = apiKeys[0].key;

            this.licenseCache = {
              data: licenseKey,
              timestamp: Date.now(),
            };

            return licenseKey;
          }
        }
        return '';
      }),
      catchError(() => {
        return of('');
      })
    );
  }

  private isCacheValid(cache: { data: any; timestamp: number }): boolean {
    return Date.now() - cache.timestamp < this.CACHE_DURATION;
  }

  clearCache(): void {
    this.profileCache = null;
    this.licenseCache = null;
  }
}
